import * as React from 'react';
import Box from '@mui/material/Box';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { Trans } from '@lingui/macro';
import { Button, SvgIcon, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { ContentWithTooltip } from 'src/components/ContentWithTooltip';
import { useRootStore } from 'src/store/root';
import { ENABLE_TESTNET } from 'src/utils/marketsAndNetworksConfig';

import { MarketSwitcher } from '../components/MarketSwitcher';
import { Link } from '../components/primitives/Link';
import { uiConfig } from '../uiConfig';
import { NavItems } from './components/NavItems';
import { MobileMenu } from './MobileMenu';
import WalletWidget from './WalletWidget';
import { useSwitchNetworkOnFocus } from '../hooks/yldr/useSwitchNetworkOnFocus';
// import { BaseBanner } from '../banners/BaseBanner';

// interface Props {
//   children: React.ReactElement;
// }

export function AppHeader() {
  const { breakpoints } = useTheme();
  const md = useMediaQuery(breakpoints.down('md'));
  const sm = useMediaQuery(breakpoints.down('sm'));

  const [mobileDrawerOpen, setMobileDrawerOpen] = useRootStore((state) => [
    state.mobileDrawerOpen,
    state.setMobileDrawerOpen,
  ]);

  const [walletWidgetOpen, setWalletWidgetOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mobileDrawerOpen && !md) {
      setMobileDrawerOpen(false);
    }
    if (walletWidgetOpen) {
      setWalletWidgetOpen(false);
    }
  }, [md]);

  const headerHeight = 64;

  const toggleWalletWidget = (state: boolean) => {
    if (md) setMobileDrawerOpen(state);
    setWalletWidgetOpen(state);
  };

  const toggleMobileMenu = (state: boolean) => {
    if (md) setMobileDrawerOpen(state);
    setMobileMenuOpen(state);
  };

  useSwitchNetworkOnFocus();

  const testnetTooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}>
      <Typography variant="subheader1">
        <Trans>Testnet mode is ON</Trans>
      </Typography>
      <Typography variant="description">
        <Trans>The app is running in testnet mode.</Trans> {/*<Link*/}
      </Typography>
    </Box>
  );

  return (
    <>
      {/*{!mobileDrawerOpen && !walletWidgetOpen && !mobileMenuOpen && (*/}
      {/*  <BaseBanner />*/}
      {/*)}*/}
      <Box
        component="header"
        sx={(theme) => ({
          height: headerHeight,
          position: 'sticky',
          top: 0,
          width: '100vw',
          transition: theme.transitions.create('top'),
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.header,
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          padding: {
            xs: '12px 16px',
            md: '16px 24px',
          },
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'space-between',
          backdropFilter: 'blur(24px)',
        })}
      >
        <Box
          component={Link}
          href="/"
          aria-label="Go to homepage"
          sx={{
            lineHeight: 0,
            mr: { xs: 2, md: 3 },
            transition: '0.3s ease all',
            '&:hover': { opacity: 0.7 },
            ['> img:first-child']: {
              display: { xs: 'block', md: 'none' },
            },
            ['> img:last-child']: {
              display: { xs: 'none', md: 'block' },
            },
          }}
          onClick={() => setMobileMenuOpen(false)}
        >
          <img src={uiConfig.mobileAppLogo } alt="YLDR"/>
          <img src={uiConfig.appLogo} alt="YLDR"/>
        </Box>
        <Box sx={{ mr: sm ? 1 : 3 }}>
          {ENABLE_TESTNET && (
            <ContentWithTooltip tooltipContent={testnetTooltip} offset={[0, -4]} withoutHover>
              <Button
                variant="surface"
                size="small"
                sx={{
                  color: '#FCB6CE',
                  backgroundColor: 'rgba(199, 0, 70, 0.16)',
                  border: '1px solid rgba(199, 0, 70, 0.32)',
                  borderRadius: '8px',
                  '&:hover, &.Mui-focusVisible': { backgroundColor: 'rgba(199, 0, 70, 0.08)' },
                }}
              >
                TESTNET
                <SvgIcon sx={{ marginLeft: '2px', fontSize: '16px' }}>
                  <InformationCircleIcon/>
                </SvgIcon>
              </Button>
            </ContentWithTooltip>
          )}
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <NavItems />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {!mobileMenuOpen && (
          <>
            <MarketSwitcher />
            <WalletWidget
              open={walletWidgetOpen}
              setOpen={toggleWalletWidget}
              headerHeight={headerHeight}
            />
          </>
        )}

        {!walletWidgetOpen && (
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <MobileMenu
              open={mobileMenuOpen}
              setOpen={toggleMobileMenu}
              headerHeight={headerHeight}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
